import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CarIcon, CheckIcon, FanIcon, HeaterIcon, RotateCcwIcon, UserIcon } from 'lucide-react';
import type { House } from '../../types/house';
import type { ContractType, UsageProfile } from '../../types/scan';
import { AIRCO_KWH, EV_KWH, HEAT_PUMP_KWH, KWH_PER_PANEL } from '../../utils/energy';
import { num } from '../../utils/format';
interface StepVerbruikProps {
  house: House;
  usage: UsageProfile;
  onChange: (patch: Partial<UsageProfile>) => void;
  onBack: () => void;
  onNext: () => void;
}
const extras = [{
  key: 'heatPump',
  label: 'Warmtepomp',
  kwh: HEAT_PUMP_KWH,
  icon: HeaterIcon
}, {
  key: 'evCharger',
  label: 'Elektrische auto',
  kwh: EV_KWH,
  icon: CarIcon
}, {
  key: 'airco',
  label: 'Airco',
  kwh: AIRCO_KWH,
  icon: FanIcon
}] as const;
const contracts: {
  value: ContractType;
  title: string;
  text: string;
}[] = [{
  value: 'vast',
  title: 'Vast of variabel',
  text: 'Eén tarief, de batterij slaat vooral zonnestroom op.'
}, {
  value: 'dynamisch',
  title: 'Dynamisch',
  text: 'Prijs per uur, de batterij handelt ook slim op het net.'
}];
export function StepVerbruik({
  house,
  usage,
  onChange,
  onBack,
  onNext
}: StepVerbruikProps) {
  const maxPanels = Math.max(house.panelPotential, 24);
  return <div data-id="element-599" data-mp="14dyco74orz70">
      <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-600" data-mp="14aakpcew3kue">Hoe gebruik je stroom?</h1>
      <p className="mt-3 text-lg text-ink-soft" data-id="element-601" data-mp="1x0syb7d8p4m2">We schatten je jaarverbruik. Weet je het precies? Pas het onderaan aan.</p>

      <fieldset className="mt-8" data-id="element-602" data-mp="212w9rvzws97c">
        <legend className="text-[15px] font-medium text-ink" data-id="element-603" data-mp="211un0u9j5y9s">Hoeveel personen wonen er?</legend>
        <div className="mt-3 grid grid-cols-5 overflow-hidden rounded-2xl border border-line bg-white" data-id="element-604" data-mp="1n1tq1pwdzazl">
          {[1, 2, 3, 4, 5].map(n => <label key={n} className={`flex cursor-pointer items-center justify-center gap-1.5 border-r border-line py-4 text-[16px] font-medium last:border-r-0 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-ink ${usage.persons === n ? 'bg-ink text-canvas' : 'text-ink hover:bg-canvas-deep'}`} data-id="element-605" data-mp="bng3qpha73lo">
              <input type="radio" name="persons" className="sr-only" checked={usage.persons === n} onChange={() => onChange({
            persons: n
          })} data-id="element-606" data-mp="3jx8zep0a6rdp" />
              {n === 5 ? '5+' : n}
              <UserIcon className="h-4 w-4 opacity-60" aria-hidden="true" data-id="element-607" data-mp="3ewl6tywem2hd" />
            </label>)}
        </div>
      </fieldset>

      <div className="mt-8 rounded-2xl border border-line bg-white p-5" data-id="element-608" data-mp="34zynxfnn1lhz">
        <div className="flex items-center justify-between gap-4" data-id="element-609" data-mp="30ymxj7wxww8q">
          <div data-id="element-610" data-mp="30ywlyr2c6vcb">
            <div className="text-[15px] font-medium text-ink" data-id="element-611" data-mp="310hhok0e0kbr">Ik heb zonnepanelen</div>
            <div className="text-[14px] text-ink-muted" data-id="element-612" data-mp="2w1mpirgj5jez">Of ik ben van plan ze te nemen</div>
          </div>
          <button type="button" role="switch" aria-checked={usage.hasSolar} aria-label="Ik heb zonnepanelen" onClick={() => onChange({
          hasSolar: !usage.hasSolar
        })} className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-150 ${usage.hasSolar ? 'bg-forest-light' : 'bg-line'}`} data-id="element-613" data-mp="z5711ue68n5u">
            <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white transition-transform duration-150 ease-out ${usage.hasSolar ? 'translate-x-5' : ''}`} data-id="element-614" data-mp="1biasynqjquse" />
          </button>
        </div>
        {usage.hasSolar && <div className="mt-5 border-t border-line pt-5" data-id="element-615" data-mp="nsggw8e1xa97">
            <div className="flex items-baseline justify-between" data-id="element-616" data-mp="sqi14wn7rfm9">
              <label htmlFor="panels" className="text-[14px] text-ink-muted" data-id="element-617" data-mp="stdxwhnbry94">Aantal panelen</label>
              <span className="text-[15px] text-ink" data-id="element-618" data-mp="wqa32qi3qnin">
                <strong className="text-xl font-semibold tabular-nums" data-id="element-619" data-mp="245erqaz1qcvq">{usage.solarPanels}</strong> panelen ·{' '}
                <span className="text-ink-muted" data-id="element-620" data-mp="28672x9rharun">± {num(usage.solarPanels * KWH_PER_PANEL)} kWh/jaar</span>
              </span>
            </div>
            <input id="panels" type="range" min={2} max={maxPanels} value={usage.solarPanels} onChange={e => onChange({
          solarPanels: Number(e.target.value)
        })} className="range mt-4 w-full" data-id="element-621" data-mp="21rrq9ru6ldd2" />
          </div>}
      </div>

      <fieldset className="mt-8" data-id="element-622" data-mp="2po5uyxzpgf10">
        <legend className="text-[15px] font-medium text-ink" data-id="element-623" data-mp="2pne23uxhsvef">Wat heb je nog meer in huis?</legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-3" data-id="element-624" data-mp="2kob4gvjudrgh">
          {extras.map(({
          key,
          label,
          kwh,
          icon: Icon
        }) => {
          const checked = usage[key];
          return <label key={key} className={`flex cursor-pointer items-center gap-3 rounded-2xl border bg-white p-4 transition-colors duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${checked ? 'border-ink' : 'border-line hover:border-ink/40'}`} data-id="element-625" data-mp="2ugz03mz35pdm">
                <input type="checkbox" className="sr-only" checked={checked} onChange={() => onChange({
              [key]: !checked
            } as Partial<UsageProfile>)} data-id="element-626" data-mp="3s29ycx1dthkr" />
                <Icon className="h-5 w-5 shrink-0 text-forest-light" aria-hidden="true" data-id="element-627" data-mp="3e1pio9qrk1vh" />
                <span className="flex-1" data-id="element-628" data-mp="3izilcssuydr0">
                  <span className="block text-[15px] font-medium text-ink" data-id="element-629" data-mp="34cfnum4jc53o">{label}</span>
                  <span className="block text-[13px] text-ink-muted" data-id="element-630" data-mp="392w0w54g1opi">+ {num(kwh)} kWh</span>
                </span>
                <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${checked ? 'border-ink bg-ink text-canvas' : 'border-line'}`} data-id="element-631" data-mp="2vbra7sj206rb">
                  {checked && <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-632" data-mp="2zb04cyi0a6ne" />}
                </span>
              </label>;
        })}
        </div>
      </fieldset>

      <fieldset className="mt-8" data-id="element-633" data-mp="2qbv4noupk79n">
        <legend className="text-[15px] font-medium text-ink" data-id="element-634" data-mp="2qe8wzpy075di">Welk energiecontract heb je?</legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-635" data-mp="vy0u9n5lg6pj">
          {contracts.map(c => <label key={c.value} className={`cursor-pointer rounded-2xl border bg-white p-4 transition-colors duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${usage.contract === c.value ? 'border-ink' : 'border-line hover:border-ink/40'}`} data-id="element-636" data-mp="r3msn30c93cf">
              <input type="radio" name="contract" className="sr-only" checked={usage.contract === c.value} onChange={() => onChange({
            contract: c.value
          })} data-id="element-637" data-mp="1at6zi7wv81ht" />
              <span className="block text-[15px] font-medium text-ink" data-id="element-638" data-mp="15wgh3dxl8tt8">{c.title}</span>
              <span className="mt-1 block text-[14px] leading-snug text-ink-muted" data-id="element-639" data-mp="10v10pz6cvbh6">{c.text}</span>
            </label>)}
        </div>
      </fieldset>

      <div className="mt-8 rounded-2xl bg-canvas-deep p-5" data-id="element-640" data-mp="3ll8exhp0prm0">
        <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-641" data-mp="3lnukxcw9fz24">
          <label htmlFor="usage" className="text-[15px] font-medium text-ink" data-id="element-642" data-mp="3bje68muionzw">
            {usage.usageEdited ? 'Jouw jaarverbruik' : 'Geschat jaarverbruik'}
          </label>
          <span className="text-3xl font-semibold tracking-tight tabular-nums text-ink" data-id="element-643" data-mp="3lez7girrhk8l">{num(usage.annualUsage)} kWh</span>
        </div>
        <input id="usage" type="range" min={1000} max={15000} step={50} value={usage.annualUsage} onChange={e => onChange({
        annualUsage: Number(e.target.value)
      })} className="range mt-4 w-full" data-id="element-644" data-mp="3q8ag8pg4wb35" />
        {usage.usageEdited && <button type="button" onClick={() => onChange({
        usageEdited: false
      })} className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-ink-soft hover:text-ink" data-id="element-645" data-mp="cznrvdv1fb5s">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-646" data-mp="2esc3aahu54wn" /> Terug naar schatting
          </button>}
      </div>

      <div className="mt-10 flex items-center justify-between" data-id="element-647" data-mp="khdlrippp2u4">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 px-2 py-3 text-[15px] font-medium text-ink-soft hover:text-ink" data-id="element-648" data-mp="fjc6l8vepnnx">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-649" data-mp="pa6qi6l6kb67" /> Terug
        </button>
        <button type="button" onClick={onNext} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-ink px-6 py-4 text-[16px] font-medium text-canvas transition-colors duration-150 hover:bg-forest" data-id="element-650" data-mp="pcj6wfbaidh5">
          Bereken mijn advies
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-651" data-mp="aj66d4b2msu8" />
        </button>
      </div>
    </div>;
}